import { ChevronRightIcon } from "@heroicons/react/outline";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { TFLocationState } from "types/topicflow";

import useLabel from "@apps/use-label/use-label";
import AppLink from "@components/link/link";
import { classNames } from "@helpers/css";
import { getUrl, toWithBackground } from "@helpers/helpers";

export type ToolCallInfo = {
  toolName: string;
  resultCount?: number | null;
  arguments?: string | null;
  output?: string | null;
};

type ResultItem = {
  id: number | string | null;
  title: string;
  url: string;
};

// Tools that return user-profile-linkable items
const USER_TOOLS = new Set(["get_user_infos"]);

// Tools where expanding to show individual items doesn't make sense
const SKIP_EXPAND_TOOLS = new Set(["load_documentation"]);

function parseDescription(tc: ToolCallInfo): string | null {
  if (!tc.arguments) return null;
  try {
    const args = JSON.parse(tc.arguments);
    return args.description || null;
  } catch {
    return null;
  }
}

function parseResultItems(tc: ToolCallInfo): ResultItem[] {
  if (!tc.output || SKIP_EXPAND_TOOLS.has(tc.toolName)) return [];
  try {
    const parsed = JSON.parse(tc.output);
    const list = Object.values(parsed).find((v) => Array.isArray(v)) as
      | Record<string, unknown>[]
      | undefined;
    if (!list) return [];
    const seen = new Set<string>();
    return list
      .filter((item) => item.id != null)
      .map((item) => ({
        id: (item.id as number | string) ?? null,
        title: String(
          item.title || item.goal_title || item.name || item.subject || "",
        ),
        url: String(item.url || item.meeting_url || ""),
      }))
      .filter((item) => {
        if (!item.title || seen.has(item.title)) return false;
        seen.add(item.title);
        return true;
      });
  } catch {
    return [];
  }
}

const ResultItemLink = ({
  item,
  isUserTool,
}: {
  item: ResultItem;
  isUserTool: boolean;
}) => {
  const location = useLocation<TFLocationState>();

  if (isUserTool && item.id) {
    return (
      <AppLink
        to={toWithBackground({
          pathname: getUrl({ profileId: item.id }),
          location,
        })}
        className="text-blue-500 hover:text-blue-700 hover:underline"
      >
        {item.title}
      </AppLink>
    );
  }

  if (item.url) {
    const isInternal =
      item.url.startsWith("/") || item.url.includes(window.location.hostname);
    if (isInternal) {
      const pathname = item.url.startsWith("/")
        ? item.url
        : new URL(item.url).pathname;
      return (
        <AppLink
          to={toWithBackground({ pathname, location })}
          className="text-blue-500 hover:text-blue-700 hover:underline"
        >
          {item.title}
        </AppLink>
      );
    }
    return (
      <a
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-500 hover:text-blue-700 hover:underline"
      >
        {item.title}
      </a>
    );
  }

  return <span className="text-gray-500">{item.title}</span>;
};

const ToolCallDetail = ({ tc, label }: { tc: ToolCallInfo; label: string }) => {
  const [expanded, setExpanded] = useState(false);
  const resultItems = parseResultItems(tc);
  const hasDetail = resultItems.length > 0;
  const isUserTool = USER_TOOLS.has(tc.toolName);

  return (
    <li className="text-xs text-gray-400">
      {hasDetail ? (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 hover:text-gray-600 transition-colors text-left"
        >
          <ChevronRightIcon
            className={classNames(
              "h-2.5 w-2.5 shrink-0 transition-transform",
              expanded && "rotate-90",
            )}
          />
          <span>{label}</span>
        </button>
      ) : (
        <span className="ml-3.5">{label}</span>
      )}
      {expanded && resultItems.length > 0 && (
        <ul className="ml-3.5 mt-1 mb-1.5 flex flex-col gap-0.5 list-disc pl-4">
          {resultItems.map((item, i) => (
            <li key={i}>
              <ResultItemLink item={item} isUserTool={isUserTool} />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

const AskAIToolCalls = ({ toolCalls }: { toolCalls: ToolCallInfo[] }) => {
  const [expanded, setExpanded] = useState(false);
  const label = useLabel();

  if (toolCalls.length === 0) return null;

  const getToolLabel = (tc: ToolCallInfo) => {
    const description = parseDescription(tc);
    const count = tc.resultCount;
    const countSuffix = count != null ? ` (${count})` : "";

    if (description) {
      return `${description}${countSuffix}`;
    }

    // Fallback labels when no description is available
    const hasCount = count != null;

    if (tc.toolName === "list_goals") {
      const name = label("goal", { pluralize: !hasCount || count !== 1 });
      return hasCount ? `Fetched ${count} ${name}` : `Fetched ${name}`;
    }
    if (tc.toolName === "list_goal_checkins") {
      return hasCount
        ? `Fetched ${count} goal check-in${count !== 1 ? "s" : ""}`
        : "Fetched goal check-ins";
    }
    if (tc.toolName === "list_action_items") {
      return hasCount
        ? `Fetched ${count} action item${count !== 1 ? "s" : ""}`
        : "Fetched action items";
    }
    if (tc.toolName === "list_feedback") {
      const name = label("feedback", { pluralize: !hasCount || count !== 1 });
      return hasCount ? `Fetched ${count} ${name}` : `Fetched ${name}`;
    }
    if (tc.toolName === "list_recognitions") {
      const name = label("recognition", {
        pluralize: !hasCount || count !== 1,
      });
      return hasCount ? `Fetched ${count} ${name}` : `Fetched ${name}`;
    }
    if (tc.toolName === "list_meetings") {
      const name = label("meeting", { pluralize: !hasCount || count !== 1 });
      return hasCount ? `Fetched ${count} ${name}` : `Fetched ${name}`;
    }
    if (tc.toolName === "list_assessments") {
      return hasCount
        ? `Fetched ${count} assessment${count !== 1 ? "s" : ""}`
        : "Fetched assessments";
    }
    if (tc.toolName === "get_user_infos") {
      const items = parseResultItems(tc);
      const userCount = items.length || count;
      return userCount
        ? `Fetched infos from ${userCount} user${userCount !== 1 ? "s" : ""}`
        : "Fetched user info";
    }
    if (tc.toolName === "query_external_events") {
      return hasCount
        ? `Fetched ${count} external event${count !== 1 ? "s" : ""}`
        : "Fetched external events";
    }
    if (tc.toolName === "load_documentation") {
      return "Loaded documentation";
    }
    return tc.toolName;
  };

  return (
    <div className="text-sm text-gray-500">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 hover:text-gray-700 transition-colors"
      >
        <ChevronRightIcon
          className={classNames(
            "h-3 w-3 transition-transform",
            expanded && "rotate-90",
          )}
        />
        <span>
          {toolCalls.length} tool call{toolCalls.length !== 1 ? "s" : ""}
        </span>
      </button>
      {expanded && (
        <ul className="ml-4 mt-1 flex flex-col gap-0.5">
          {toolCalls.map((tc, index) => (
            <ToolCallDetail key={index} tc={tc} label={getToolLabel(tc)} />
          ))}
        </ul>
      )}
    </div>
  );
};

export default AskAIToolCalls;
